// control-center/src/pages/GiteaBotCredentials.tsx
import { useEffect, useState, useCallback } from 'react';
import {
  listBots, mintForBot, createBot as apiCreate, revokePat, type GiteaBotRow,
} from '../lib/giteaBotCredentials';
import { GiteaBotRow as RowComponent } from '../components/credentials/GiteaBotRow';
import { MintPatDialog } from '../components/credentials/MintPatDialog';
import { RevealPatDialog } from '../components/credentials/RevealPatDialog';
import { CreateBotDialog } from '../components/credentials/CreateBotDialog';
import { RevokePatDialog } from '../components/credentials/RevokePatDialog';

interface RevealState {
  patName: string;
  vaultPath: string;
  value: string;
}

export function GiteaBotCredentials() {
  const [bots, setBots] = useState<GiteaBotRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reveal, setReveal] = useState<RevealState | null>(null);
  const [mintTarget, setMintTarget] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<{ botName: string; patName: string } | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { bots: data } = await listBots();
      setBots(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const onMint = async (bot: string) => {
    try {
      const r = await mintForBot(bot);
      setMintTarget(null);
      setReveal({ patName: r.pat_name, vaultPath: r.vault_path, value: r._value ?? '' });
      refresh();
    } catch (e) {
      setError(`Mint failed for ${bot}: ${(e as Error).message}`);
      setMintTarget(null);  // close dialog even on error
    }
  };

  const onCreate = async (body: { bot_name: string; product: string; ssh_pubkey?: string }) => {
    try {
      const r = await apiCreate(body);
      setShowCreate(false);
      setReveal({ patName: r.pat_name, vaultPath: r.vault_path, value: r._value ?? '' });
      refresh();
    } catch (e) {
      setError(`Create bot failed: ${(e as Error).message}`);
      setShowCreate(false);  // close dialog even on error
    }
  };

  const onRevoke = async (patName: string) => {
    if (!revokeTarget) return;
    try {
      await revokePat(revokeTarget.botName, patName);
      setRevokeTarget(null);
      refresh();
    } catch (e) {
      setError(`Revoke failed for ${revokeTarget.botName}/${patName}: ${(e as Error).message}`);
      setRevokeTarget(null);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0, color: '#e2e8f0' }}>Gitea Bots</h1>
        <button
          onClick={() => setShowCreate(true)}
          style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, padding: '0.5rem 1.25rem', cursor: 'pointer', fontWeight: 600 }}
        >
          + New Bot
        </button>
      </div>

      {error && <div style={{ background: '#7f1d1d', color: '#fca5a5', borderRadius: 6, padding: '0.75rem', marginBottom: '1rem' }}>{error}</div>}
      {loading && <div style={{ color: '#64748b' }}>Loading...</div>}

      {!loading && bots.length === 0 && (
        <div style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>
          No bots registered. Click &quot;+ New Bot&quot; to create one.
        </div>
      )}

      {!loading && bots.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#1e293b', borderRadius: 8 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #334155' }}>
              <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', color: '#64748b', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>Bot</th>
              <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', color: '#64748b', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>Product</th>
              <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', color: '#64748b', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>Scopes</th>
              <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', color: '#64748b', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>Last Rotated</th>
              <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', color: '#64748b', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>Status</th>
              <th style={{ padding: '0.6rem 0.75rem' }}></th>
            </tr>
          </thead>
          <tbody>
            {bots.map(b => (
              <RowComponent
                key={b.id}
                bot={b}
                onMint={() => setMintTarget(b.bot_name)}
              />
            ))}
          </tbody>
        </table>
      )}

      {mintTarget && (
        <MintPatDialog
          botName={mintTarget}
          onConfirm={() => onMint(mintTarget)}
          onCancel={() => setMintTarget(null)}
        />
      )}
      {showCreate && (
        <CreateBotDialog
          onSubmit={onCreate}
          onCancel={() => setShowCreate(false)}
        />
      )}
      {revokeTarget && (
        <RevokePatDialog
          botName={revokeTarget.botName}
          patName={revokeTarget.patName}
          onConfirm={onRevoke}
          onCancel={() => setRevokeTarget(null)}
        />
      )}
      {reveal && (
        <RevealPatDialog
          open={true}
          patName={reveal.patName}
          vaultPath={reveal.vaultPath}
          patValue={reveal.value}
          onClose={() => setReveal(null)}
        />
      )}
    </div>
  );
}
